import { create } from 'zustand';
import type { Message, MessageReaction } from '@disenchantment/types';

// ── Ordering ────────────────────────────────────────────────────────────────
// Deterministic sort: createdAt ASC, id ASC as tiebreaker.
// Applied after every mutation so ordering is always correct regardless of
// whether messages arrived via REST, real-time, or pagination.
function sortMessages(msgs: Message[]): Message[] {
  return [...msgs].sort((a, b) => {
    const td = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return td !== 0 ? td : a.id.localeCompare(b.id);
  });
}

// Collect author name from a message into the names cache.
function collectName(names: Record<string, string>, msg: Message): Record<string, string> {
  const name = msg.author.displayName || msg.author.username;
  if (names[msg.author.id] === name) return names; // avoid unnecessary new objects
  return { ...names, [msg.author.id]: name };
}

interface ChatState {
  // ── Messages ──────────────────────────────────────────────────────────────
  messagesByChannel: Record<string, Message[]>;
  typingByChannel: Record<string, Set<string>>;

  // ── Reply state ────────────────────────────────────────────────────────────
  /** The message currently being replied to. Shown as a banner in MessageInput. */
  replyTo: Message | null;
  setReplyTo: (message: Message | null) => void;

  // ── Member name cache (for typing indicator labels) ───────────────────────
  /** userId → displayName. Populated from message authors as they load. */
  memberNames: Record<string, string>;

  // ── Pagination state ───────────────────────────────────────────────────────
  hasMoreByChannel: Record<string, boolean>;
  loadingMoreByChannel: Record<string, boolean>;
  setHasMore: (channelId: string, value: boolean) => void;
  setLoadingMore: (channelId: string, value: boolean) => void;

  // ── Actions ────────────────────────────────────────────────────────────────
  setMessages: (channelId: string, messages: Message[]) => void;
  prependMessages: (channelId: string, messages: Message[]) => void;
  appendMessage: (channelId: string, message: Message) => void;
  updateMessage: (channelId: string, updated: Message) => void;
  deleteMessage: (channelId: string, messageId: string) => void;
  updateMessageReactions: (channelId: string, messageId: string, reactions: MessageReaction[]) => void;
  setTyping: (channelId: string, userId: string, isTyping: boolean) => void;
  /** Clear all typing indicators across all channels (called on socket disconnect). */
  clearAllTyping: () => void;
  clear: () => void;
}

export const useChatStore = create<ChatState>((set) => ({
  messagesByChannel: {},
  typingByChannel: {},
  replyTo: null,
  memberNames: {},
  hasMoreByChannel: {},
  loadingMoreByChannel: {},

  setReplyTo: (message) => set({ replyTo: message }),

  setHasMore: (channelId, value) =>
    set((s) => ({ hasMoreByChannel: { ...s.hasMoreByChannel, [channelId]: value } })),

  setLoadingMore: (channelId, value) =>
    set((s) => ({ loadingMoreByChannel: { ...s.loadingMoreByChannel, [channelId]: value } })),

  setMessages: (channelId, messages) =>
    set((s) => {
      const existing = s.messagesByChannel[channelId] ?? [];
      const incomingIds = new Set(messages.map((m) => m.id));
      const realtimeOnly = existing.filter((m) => !incomingIds.has(m.id));
      const merged = sortMessages([...messages, ...realtimeOnly]);
      let names = s.memberNames;
      merged.forEach((m) => { names = collectName(names, m); });
      return { messagesByChannel: { ...s.messagesByChannel, [channelId]: merged }, memberNames: names };
    }),

  prependMessages: (channelId, messages) =>
    set((s) => {
      const existing = s.messagesByChannel[channelId] ?? [];
      const existingIds = new Set(existing.map((m) => m.id));
      const newOnly = messages.filter((m) => !existingIds.has(m.id));
      const merged = sortMessages([...newOnly, ...existing]);
      let names = s.memberNames;
      newOnly.forEach((m) => { names = collectName(names, m); });
      return { messagesByChannel: { ...s.messagesByChannel, [channelId]: merged }, memberNames: names };
    }),

  appendMessage: (channelId, message) =>
    set((s) => {
      const existing = s.messagesByChannel[channelId] ?? [];
      const idx = existing.findIndex((m) => m.id === message.id);
      const without = idx === -1 ? existing : existing.filter((_, i) => i !== idx);
      const next = sortMessages([...without, message]);
      const names = collectName(s.memberNames, message);
      return { messagesByChannel: { ...s.messagesByChannel, [channelId]: next }, memberNames: names };
    }),

  updateMessage: (channelId, updated) =>
    set((s) => {
      const messages = sortMessages(
        (s.messagesByChannel[channelId] ?? []).map((m) => (m.id === updated.id ? updated : m))
      );
      return { messagesByChannel: { ...s.messagesByChannel, [channelId]: messages } };
    }),

  deleteMessage: (channelId, messageId) =>
    set((s) => {
      const messages = (s.messagesByChannel[channelId] ?? []).filter((m) => m.id !== messageId);
      return { messagesByChannel: { ...s.messagesByChannel, [channelId]: messages } };
    }),

  updateMessageReactions: (channelId, messageId, reactions) =>
    set((s) => {
      const messages = (s.messagesByChannel[channelId] ?? []).map((m) =>
        m.id === messageId ? { ...m, reactions } : m
      );
      return { messagesByChannel: { ...s.messagesByChannel, [channelId]: messages } };
    }),

  setTyping: (channelId, userId, isTyping) =>
    set((s) => {
      const set_ = new Set(s.typingByChannel[channelId]);
      if (isTyping) set_.add(userId);
      else set_.delete(userId);
      return { typingByChannel: { ...s.typingByChannel, [channelId]: set_ } };
    }),

  clearAllTyping: () => set({ typingByChannel: {} }),

  clear: () =>
    set({
      messagesByChannel: {},
      typingByChannel: {},
      replyTo: null,
      hasMoreByChannel: {},
      loadingMoreByChannel: {},
    }),
}));
